import { useEffect, useState } from "react";
import { format } from "date-fns";
import type { Availability } from "@shyne/shared/api";
import { IndividualScheduleRepository } from "@/repositories/individual-schedule.repository";
import { DAYS_OF_WEEK, type DayOfWeek } from "@/models/individual-schedule.model";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Calendar, CalendarX, CheckCircle2, Clock, User, XCircle } from "lucide-react";
import { uuid } from "@/interfaces";

type WorkerAvailabilityProps = {
  contractId: uuid;
  workerName: string;
};

const SHORT_NAMES: Record<DayOfWeek, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

export function WorkerAvailability({ contractId, workerName }: WorkerAvailabilityProps) {
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    IndividualScheduleRepository.ofWorkerContract(contractId)
      .then((answer) => {
        if (mounted) setAvailability(answer);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : "Failed to load schedule");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [contractId]);

  if (loading) {
    return (
      <div className="grid grid-cols-7 gap-3">
        {DAYS_OF_WEEK.map((day) => (
          <div key={day} className="bg-muted/50 rounded-lg p-3 space-y-2">
            <Skeleton className="h-3 w-8 mx-auto" />
            <Skeleton className="h-2 w-10 mx-auto" />
            <Skeleton className="h-2 w-10 mx-auto" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive/50 bg-destructive/5">
        <CardContent className="flex items-center gap-3 p-4">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-destructive">Error loading schedule</p>
            <p className="text-xs text-destructive/80">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!availability || availability.updatedAt === null) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <CalendarX className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-lg mb-2">No Schedule Available</h3>
          <p className="text-muted-foreground text-sm">{workerName} hasn't set up their work schedule yet.</p>
        </CardContent>
      </Card>
    );
  }

  const workingDays = DAYS_OF_WEEK.filter((day) => availability.days[day] !== null);

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-base">Weekly Schedule</h3>
        </div>

        <ul aria-label="Weekly availability" className="grid grid-cols-7 gap-3">
          {DAYS_OF_WEEK.map((day) => {
            const hours = availability.days[day];
            return (
              <li
                key={day}
                aria-label={SHORT_NAMES[day]}
                className={`relative px-2 py-2.5 rounded-lg border transition-all ${hours ? "bg-primary/5 border-primary/20 shadow-sm" : "bg-muted/30 border-muted/40"}`}
              >
                <div className="text-center space-y-0.5">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{SHORT_NAMES[day]}</div>
                  {hours ? (
                    <>
                      <div className="flex items-center justify-center mb-0.5">
                        <CheckCircle2 className="h-3 w-3 text-primary" />
                      </div>
                      <div className="text-xs font-semibold text-foreground">{hours.start}</div>
                      <div className="text-xs text-muted-foreground">—</div>
                      <div className="text-xs font-semibold text-foreground">{hours.end}</div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-center mb-0.5">
                        <XCircle className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div className="text-xs text-muted-foreground font-medium">Off</div>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <Card className="border-primary/10 bg-gradient-to-br from-primary/5 to-primary/10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">{workingDays.length}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Working Days</div>
            </div>
          </div>
          {workingDays.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {workingDays.map((day) => (
                <Badge key={day} variant="secondary" className="text-xs bg-primary/10 text-primary border-primary/20">
                  {SHORT_NAMES[day]}
                </Badge>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground italic">No working days scheduled</div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-center gap-2 pt-3 border-t border-muted/30">
        <Clock className="h-3 w-3 text-muted-foreground/60" />
        <span className="text-xs text-muted-foreground">Last updated {format(new Date(availability.updatedAt), "MMM d, yyyy 'at' h:mm a")}</span>
      </div>
    </div>
  );
}
